import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";

import { Loader2 } from "lucide-react";
import { initTracking } from "@/hooks/useTrackingData";
import { LocationProvider } from "@/contexts/LocationContext";
import { DeferredMount } from "@/components/shared/DeferredMount";

// Capture UTM params and referrer on first load
initTracking();

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
}

// Eager load homepage for fast initial paint
import Index from "./pages/Index";

/**
 * Lazy loader that survives stale chunk hashes after a new deploy.
 * Retries once; if the chunk is still missing, force a hard reload (once).
 */
function lazyPage<T extends { default: React.ComponentType<any> }>(factory: () => Promise<T>) {
  return lazy(() =>
    factory().catch(async (err) => {
      try {
        return await factory();
      } catch {
        const key = "chunk-reload";
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          window.location.reload();
          return new Promise<T>(() => {});
        }
        throw err;
      }
    }),
  );
}

// Lazy load all other pages for code splitting

const Shop = lazyPage(() => import("./pages/Shop"));
const ProductPage = lazyPage(() => import("./pages/ProductPage"));
const BulkQuote = lazyPage(() => import("./pages/BulkQuote"));
const About = lazyPage(() => import("./pages/About"));
const Contact = lazyPage(() => import("./pages/Contact"));
const Resources = lazyPage(() => import("./pages/Resources"));
const ConnectAgent = lazyPage(() => import("./pages/ConnectAgent"));
const NotFound = lazyPage(() => import("./pages/NotFound"));
const AreaCalculator = lazyPage(() => import("./pages/resources/AreaCalculator"));
const InstallationGuides = lazyPage(() => import("./pages/resources/InstallationGuides"));
const LocationPage = lazyPage(() => import("./pages/locations/LocationPage"));
const ArtificialGrass = lazyPage(() => import("./pages/ArtificialGrass"));
const WeedMats = lazyPage(() => import("./pages/WeedMats"));
const Quote = lazyPage(() => import("./pages/Quote"));
const QuotesDashboard = lazyPage(() => import("./pages/admin/QuotesDashboard"));
const Blog = lazyPage(() => import("./pages/Blog"));
const BlogPost = lazyPage(() => import("./pages/BlogPost"));
const BlogDashboard = lazyPage(() => import("./pages/admin/BlogDashboard"));
const LeadImport = lazyPage(() => import("./pages/admin/LeadImport"));
const LeadDetail = lazyPage(() => import("./pages/admin/LeadDetail"));
const Login = lazyPage(() => import("./pages/Login"));
const OAuthConsent = lazyPage(() => import("./pages/OAuthConsent"));
const WPCExternalCladding = lazyPage(() => import("./pages/WPCExternalCladding"));
const WPCCladdingProduct = lazyPage(() => import("./pages/WPCCladdingProduct"));
const IndoorCladdingHub = lazyPage(() => import("./pages/IndoorCladdingHub"));
const PSCladdingPage = lazyPage(() => import("./pages/PSCladdingPage"));
const PSCladdingProductPage = lazyPage(() => import("./pages/PSCladdingProductPage"));
const PVCWetWallPage = lazyPage(() => import("./pages/PVCWetWallPage"));
const PVCWetWallProductPage = lazyPage(() => import("./pages/PVCWetWallProductPage"));
const AcousticCladdingPage = lazyPage(() => import("./pages/AcousticCladdingPage"));
const AcousticCladdingProductPage = lazyPage(() => import("./pages/AcousticCladdingProductPage"));
const SPCFlooringPage = lazyPage(() => import("./pages/SPCFlooringPage"));
const SPCFlooringProductPage = lazyPage(() => import("./pages/SPCFlooringProductPage"));
const UVMarbleSheetsPage = lazyPage(() => import("./pages/UVMarbleSheetsPage"));
const FlexibleWallCladdingPage = lazyPage(() => import("./pages/FlexibleWallCladdingPage"));
const FlexibleWallCladdingProductPage = lazyPage(() => import("./pages/FlexibleWallCladdingProductPage"));
const UVMarbleSheetProductPage = lazyPage(() => import("./pages/UVMarbleSheetProductPage"));
const HedgesPage = lazyPage(() => import("./pages/HedgesPage"));
const HedgeProductPage = lazyPage(() => import("./pages/HedgeProductPage"));
const GardenPebblesPage = lazyPage(() => import("./pages/GardenPebblesPage"));
const GardenPebbleProductPage = lazyPage(() => import("./pages/GardenPebbleProductPage"));
const CladdingAccessoryProductPage = lazyPage(() => import("./pages/CladdingAccessoryProductPage"));
const WetWallAccessoryPage = lazyPage(() => import("./pages/WetWallAccessoryPage"));
const CartQuote = lazyPage(() => import("./pages/CartQuote"));
const MerchantFeed = lazyPage(() => import("./pages/MerchantFeed"));
const GeoLandingPage = lazyPage(() => import("./pages/GeoLandingPage"));
const OurPicks = lazyPage(() => import("./pages/OurPicks"));
const Reviews = lazyPage(() => import("./pages/Reviews"));
const PrivacyPolicy = lazyPage(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazyPage(() => import("./pages/TermsConditions"));
const WpcDeckingWarranty = lazyPage(() => import("./pages/WpcDeckingWarranty"));
const ArtificialGrassWarranty = lazyPage(() => import("./pages/ArtificialGrassWarranty"));
const HybridFlooringWarranty = lazyPage(() => import("./pages/HybridFlooringWarranty"));
const RefundReturnPolicy = lazyPage(() => import("./pages/RefundReturnPolicy"));
const ShippingDeliveryPolicy = lazyPage(() => import("./pages/ShippingDeliveryPolicy"));
const ArtificialGrassQuote = lazyPage(() => import("./pages/ArtificialGrassQuote"));
const ThankYou = lazyPage(() => import("./pages/ThankYou"));
// Non-critical widgets: split out of the main bundle and mounted after idle.
const ChatBot = lazyPage(() => import("@/components/chat/ChatBot").then((m) => ({ default: m.ChatBot })));
const DiscountOfferPopup = lazyPage(() => import("@/components/shop/DiscountOfferPopup").then((m) => ({ default: m.DiscountOfferPopup })));
const LocationSelectorModal = lazy(() =>
  import("@/components/location/LocationSelectorModal").then((m) => ({ default: m.LocationSelectorModal })),
);
const queryClient = new QueryClient();

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

// Redirect helpers that preserve URL params
function RedirectWithProductId({ to }: { to: string }) {
  const { productId } = useParams();
  return <Navigate to={productId ? `${to}/${productId}` : to} replace />;
}
function RedirectWithAccessoryId({ to }: { to: string }) {
  const { accessoryId } = useParams();
  return <Navigate to={accessoryId ? `${to}/${accessoryId}` : to} replace />;
}

function AppContent() {

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* ===== PRIMARY ROUTES (Clean SEO URLs) ===== */}
        <Route path="/" element={<Index />} />
        <Route path="/our-picks" element={<OurPicks />} />
        <Route path="/reviews" element={<Reviews />} />
        <Route path="/artificial-grass-quote" element={<ArtificialGrassQuote />} />
        <Route path="/thank-you" element={<ThankYou />} />
        
        {/* Category Pages — Top-Level */}
        <Route path="/artificial-grass" element={<ArtificialGrass />} />
        
        {/* Geo Landing Pages (Local SEO — Artificial Grass) */}
        <Route path="/artificial-grass-nsw" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-victoria" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-south-australia" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-queensland" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-sydney" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-melbourne" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-adelaide" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-brisbane" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-blacktown" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-penrith" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-parramatta" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-rooty-hill" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-north-lakes" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-redcliffe" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-forest-lake" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-ipswich" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-werribee" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-point-cook" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-tarneit" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-hoppers-crossing" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-geelong" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-prospect" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-port-adelaide" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-hindmarsh" element={<GeoLandingPage />} />
        <Route path="/artificial-grass-dandenong" element={<GeoLandingPage />} />

        {/* Geo Landing Pages (Local SEO — WPC Decking) */}
        <Route path="/wpc-decking-nsw" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-victoria" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-south-australia" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-queensland" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-sydney" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-melbourne" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-adelaide" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-brisbane" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-blacktown" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-penrith" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-parramatta" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-rooty-hill" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-north-lakes" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-redcliffe" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-forest-lake" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-ipswich" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-werribee" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-point-cook" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-tarneit" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-hoppers-crossing" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-geelong" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-prospect" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-port-adelaide" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-hindmarsh" element={<GeoLandingPage />} />
        <Route path="/wpc-decking-dandenong" element={<GeoLandingPage />} />

        {/* Geo Landing Pages (Local SEO — Hybrid Flooring) */}
        <Route path="/hybrid-flooring-nsw" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-victoria" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-south-australia" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-queensland" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-sydney" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-melbourne" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-adelaide" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-brisbane" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-blacktown" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-penrith" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-parramatta" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-rooty-hill" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-north-lakes" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-redcliffe" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-forest-lake" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-ipswich" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-werribee" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-point-cook" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-tarneit" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-hoppers-crossing" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-geelong" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-prospect" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-port-adelaide" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-hindmarsh" element={<GeoLandingPage />} />
        <Route path="/hybrid-flooring-dandenong" element={<GeoLandingPage />} />

        {/* Geo Landing Pages (Local SEO — PVC Wet Wall Panels) */}
        <Route path="/pvc-wet-wall-panels-nsw" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-victoria" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-south-australia" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-queensland" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-sydney" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-melbourne" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-adelaide" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-brisbane" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-blacktown" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-penrith" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-parramatta" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-rooty-hill" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-north-lakes" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-redcliffe" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-forest-lake" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-ipswich" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-werribee" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-point-cook" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-tarneit" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-hoppers-crossing" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-geelong" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-prospect" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-port-adelaide" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-hindmarsh" element={<GeoLandingPage />} />
        <Route path="/pvc-wet-wall-panels-dandenong" element={<GeoLandingPage />} />

        {/* Geo Landing Pages (Local SEO — WPC External Cladding) */}
        <Route path="/wpc-external-cladding-nsw" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-victoria" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-south-australia" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-queensland" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-sydney" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-melbourne" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-adelaide" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-brisbane" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-blacktown" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-penrith" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-parramatta" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-rooty-hill" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-north-lakes" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-redcliffe" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-forest-lake" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-ipswich" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-werribee" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-point-cook" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-tarneit" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-hoppers-crossing" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-geelong" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-prospect" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-port-adelaide" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-hindmarsh" element={<GeoLandingPage />} />
        <Route path="/wpc-external-cladding-dandenong" element={<GeoLandingPage />} />

        {/* Geo Landing Pages (Local SEO — PS Cladding) */}
        <Route path="/ps-cladding-nsw" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-victoria" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-south-australia" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-queensland" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-sydney" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-melbourne" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-adelaide" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-brisbane" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-blacktown" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-penrith" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-parramatta" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-rooty-hill" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-north-lakes" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-redcliffe" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-forest-lake" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-ipswich" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-werribee" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-point-cook" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-tarneit" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-hoppers-crossing" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-geelong" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-prospect" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-port-adelaide" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-hindmarsh" element={<GeoLandingPage />} />
        <Route path="/ps-cladding-dandenong" element={<GeoLandingPage />} />

        {/* Geo Landing Pages (Local SEO — Acoustic Cladding) */}
        <Route path="/acoustic-cladding-nsw" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-victoria" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-south-australia" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-queensland" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-sydney" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-melbourne" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-adelaide" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-brisbane" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-blacktown" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-penrith" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-parramatta" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-rooty-hill" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-north-lakes" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-redcliffe" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-forest-lake" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-ipswich" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-werribee" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-point-cook" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-tarneit" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-hoppers-crossing" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-geelong" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-prospect" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-port-adelaide" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-hindmarsh" element={<GeoLandingPage />} />
        <Route path="/acoustic-cladding-dandenong" element={<GeoLandingPage />} />

        {/* Geo Landing Pages (Local SEO — UV Marble Sheets) */}
        <Route path="/uv-marble-sheets-nsw" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-victoria" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-south-australia" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-queensland" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-sydney" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-melbourne" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-adelaide" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-brisbane" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-blacktown" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-penrith" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-parramatta" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-rooty-hill" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-north-lakes" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-redcliffe" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-forest-lake" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-ipswich" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-werribee" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-point-cook" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-tarneit" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-hoppers-crossing" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-geelong" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-prospect" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-port-adelaide" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-hindmarsh" element={<GeoLandingPage />} />
        <Route path="/uv-marble-sheets-dandenong" element={<GeoLandingPage />} />

        {/* Geo Landing Pages (Local SEO — Hedges) */}
        <Route path="/hedges-nsw" element={<GeoLandingPage />} />
        <Route path="/hedges-victoria" element={<GeoLandingPage />} />
        <Route path="/hedges-south-australia" element={<GeoLandingPage />} />
        <Route path="/hedges-queensland" element={<GeoLandingPage />} />
        <Route path="/hedges-sydney" element={<GeoLandingPage />} />
        <Route path="/hedges-melbourne" element={<GeoLandingPage />} />
        <Route path="/hedges-adelaide" element={<GeoLandingPage />} />
        <Route path="/hedges-brisbane" element={<GeoLandingPage />} />
        <Route path="/hedges-blacktown" element={<GeoLandingPage />} />
        <Route path="/hedges-penrith" element={<GeoLandingPage />} />
        <Route path="/hedges-parramatta" element={<GeoLandingPage />} />
        <Route path="/hedges-rooty-hill" element={<GeoLandingPage />} />
        <Route path="/hedges-north-lakes" element={<GeoLandingPage />} />
        <Route path="/hedges-redcliffe" element={<GeoLandingPage />} />
        <Route path="/hedges-forest-lake" element={<GeoLandingPage />} />
        <Route path="/hedges-ipswich" element={<GeoLandingPage />} />
        <Route path="/hedges-werribee" element={<GeoLandingPage />} />
        <Route path="/hedges-point-cook" element={<GeoLandingPage />} />
        <Route path="/hedges-tarneit" element={<GeoLandingPage />} />
        <Route path="/hedges-hoppers-crossing" element={<GeoLandingPage />} />
        <Route path="/hedges-geelong" element={<GeoLandingPage />} />
        <Route path="/hedges-prospect" element={<GeoLandingPage />} />
        <Route path="/hedges-port-adelaide" element={<GeoLandingPage />} />
        <Route path="/hedges-hindmarsh" element={<GeoLandingPage />} />
        <Route path="/hedges-dandenong" element={<GeoLandingPage />} />

        {/* Geo Landing Pages (Local SEO — Weed Mats; no NSW/Sydney pages) */}
        <Route path="/weed-mats-victoria" element={<GeoLandingPage />} />
        <Route path="/weed-mats-south-australia" element={<GeoLandingPage />} />
        <Route path="/weed-mats-queensland" element={<GeoLandingPage />} />
        <Route path="/weed-mats-melbourne" element={<GeoLandingPage />} />
        <Route path="/weed-mats-adelaide" element={<GeoLandingPage />} />
        <Route path="/weed-mats-brisbane" element={<GeoLandingPage />} />
        <Route path="/weed-mats-north-lakes" element={<GeoLandingPage />} />
        <Route path="/weed-mats-redcliffe" element={<GeoLandingPage />} />
        <Route path="/weed-mats-forest-lake" element={<GeoLandingPage />} />
        <Route path="/weed-mats-ipswich" element={<GeoLandingPage />} />
        <Route path="/weed-mats-werribee" element={<GeoLandingPage />} />
        <Route path="/weed-mats-point-cook" element={<GeoLandingPage />} />
        <Route path="/weed-mats-tarneit" element={<GeoLandingPage />} />
        <Route path="/weed-mats-hoppers-crossing" element={<GeoLandingPage />} />
        <Route path="/weed-mats-geelong" element={<GeoLandingPage />} />
        <Route path="/weed-mats-prospect" element={<GeoLandingPage />} />
        <Route path="/weed-mats-port-adelaide" element={<GeoLandingPage />} />
        <Route path="/weed-mats-hindmarsh" element={<GeoLandingPage />} />
        <Route path="/weed-mats-dandenong" element={<GeoLandingPage />} />

        {/* Geo Landing Pages (Local SEO — Garden Pebbles) */}
        <Route path="/garden-pebbles-nsw" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-victoria" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-south-australia" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-queensland" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-sydney" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-melbourne" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-adelaide" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-brisbane" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-blacktown" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-penrith" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-parramatta" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-rooty-hill" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-north-lakes" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-redcliffe" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-forest-lake" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-ipswich" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-werribee" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-point-cook" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-tarneit" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-hoppers-crossing" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-geelong" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-prospect" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-port-adelaide" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-hindmarsh" element={<GeoLandingPage />} />
        <Route path="/garden-pebbles-dandenong" element={<GeoLandingPage />} />

        <Route path="/wpc-decking" element={<Shop fixedCategory="wpc-decking" />} />
        <Route path="/wpc-external-cladding" element={<WPCExternalCladding />} />
        <Route path="/wpc-external-cladding/product/:productId" element={<WPCCladdingProduct />} />
        <Route path="/indoor-cladding" element={<IndoorCladdingHub />} />
        <Route path="/ps-cladding" element={<PSCladdingPage />} />
        <Route path="/ps-cladding/:productId" element={<PSCladdingProductPage />} />
        <Route path="/acoustic-cladding" element={<AcousticCladdingPage />} />
        <Route path="/acoustic-cladding/:productId" element={<AcousticCladdingProductPage />} />
        <Route path="/pvc-wet-wall-panels" element={<PVCWetWallPage />} />
        <Route path="/pvc-wet-wall-panels/:productId" element={<PVCWetWallProductPage />} />
        {/* Legacy PVC wet-wall aliases — client-side 302 fallback until an edge/HTTP 301 is activated. */}
        <Route path="/pvc-wet-wall" element={<Navigate to="/pvc-wet-wall-panels" replace />} />
        <Route path="/pvc-wet-wall/:productId" element={<RedirectWithProductId to="/pvc-wet-wall-panels" />} />
        <Route path="/shop/indoor-cladding/pvc-wet-wall" element={<Navigate to="/pvc-wet-wall-panels" replace />} />
        <Route path="/shop/indoor-cladding/pvc-wet-wall/:productId" element={<RedirectWithProductId to="/pvc-wet-wall-panels" />} />
        <Route path="/wet-wall-accessories/:accessoryId" element={<WetWallAccessoryPage />} />
        <Route path="/hybrid-flooring" element={<SPCFlooringPage />} />
        <Route path="/hybrid-flooring/:productId" element={<SPCFlooringProductPage />} />
        <Route path="/flexible-wall-cladding" element={<FlexibleWallCladdingPage />} />
        <Route path="/flexible-wall-cladding/:productId" element={<FlexibleWallCladdingProductPage />} />
        <Route path="/shop/flexible-wall-cladding" element={<Navigate to="/flexible-wall-cladding" replace />} />
        <Route path="/shop/indoor-cladding/flexible-wall-cladding" element={<Navigate to="/flexible-wall-cladding" replace />} />
        <Route path="/uv-marble-sheets" element={<UVMarbleSheetsPage />} />
        <Route path="/uv-marble-sheets/:productId" element={<UVMarbleSheetProductPage />} />
        <Route path="/hedges" element={<HedgesPage />} />
        <Route path="/hedges/:productId" element={<HedgeProductPage />} />
        <Route path="/weed-mats" element={<WeedMats />} />
        <Route path="/garden-pebbles" element={<GardenPebblesPage />} />
        <Route path="/garden-pebbles/:productId" element={<GardenPebbleProductPage />} />
        <Route path="/accessories" element={<Shop fixedCategory="accessories" />} />
        <Route path="/cladding-accessories/:accessoryId" element={<CladdingAccessoryProductPage />} />
        
        {/* Product Detail (Shopify handle) */}
        <Route path="/product/:handle" element={<ProductPage />} />
        
        {/* Utility Pages */}
        <Route path="/quote" element={<Quote />} />
        <Route path="/cart-quote" element={<CartQuote />} />
        <Route path="/bulk-quote" element={<BulkQuote />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/connect" element={<ConnectAgent />} />
        <Route path="/resources/calculator" element={<AreaCalculator />} />
        <Route path="/resources/guides" element={<InstallationGuides />} />
        <Route path="/locations/:locationId" element={<LocationPage />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/admin/quotes" element={<QuotesDashboard />} />
        <Route path="/admin/blog" element={<BlogDashboard />} />
        <Route path="/admin/lead-import" element={<LeadImport />} />
        <Route path="/admin/leads/:id" element={<LeadDetail />} />
        <Route path="/admin/merchant-feed" element={<MerchantFeed />} />
        <Route path="/login" element={<Login />} />
        <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />


        {/* ===== 301 REDIRECTS (Old URLs → New Clean URLs) ===== */}
        
        {/* All Products page */}
        <Route path="/shop" element={<Shop />} />
        <Route path="/shop/wpc-decking" element={<Navigate to="/wpc-decking" replace />} />
        <Route path="/shop/accessories" element={<Navigate to="/accessories" replace />} />
        <Route path="/shop/artificial-grass" element={<Navigate to="/artificial-grass" replace />} />
        <Route path="/shop/wpc-external-cladding" element={<Navigate to="/wpc-external-cladding" replace />} />
        
        {/* Indoor cladding flatten */}
        <Route path="/shop/indoor-cladding" element={<Navigate to="/indoor-cladding" replace />} />
        <Route path="/indoor-cladding/product/:productId" element={<Navigate to="/wpc-external-cladding" replace />} />
        <Route path="/shop/indoor-cladding/ps-cladding" element={<Navigate to="/ps-cladding" replace />} />
        <Route path="/shop/indoor-cladding/ps-cladding/:productId" element={<RedirectWithProductId to="/ps-cladding" />} />
        <Route path="/shop/indoor-cladding/acoustic-cladding" element={<Navigate to="/acoustic-cladding" replace />} />
        <Route path="/shop/indoor-cladding/acoustic-cladding/:productId" element={<RedirectWithProductId to="/acoustic-cladding" />} />
        <Route path="/shop/indoor-cladding/acoustic-panel" element={<Navigate to="/acoustic-cladding" replace />} />
        <Route path="/shop/acoustic-panels" element={<Navigate to="/acoustic-cladding" replace />} />
        <Route path="/shop/indoor-cladding/marble-sheet" element={<Navigate to="/uv-marble-sheets" replace />} />
        
        {/* SPC → Hybrid Flooring */}
        <Route path="/shop/spc-flooring" element={<Navigate to="/hybrid-flooring" replace />} />
        <Route path="/shop/spc-flooring/:productId" element={<RedirectWithProductId to="/hybrid-flooring" />} />
        
        {/* Hedges */}
        <Route path="/shop/hedges" element={<Navigate to="/hedges" replace />} />
        <Route path="/shop/hedges/:productId" element={<RedirectWithProductId to="/hedges" />} />
        <Route path="/shop/hedges-screens" element={<Navigate to="/hedges" replace />} />
        
        {/* UV Marble Sheets */}
        <Route path="/shop/uv-marble-sheets" element={<Navigate to="/uv-marble-sheets" replace />} />
        <Route path="/shop/uv-marble-sheets/:productId" element={<RedirectWithProductId to="/uv-marble-sheets" />} />
        
        {/* PVC Wet Wall → PVC Wet Wall Panels */}
        <Route path="/pvc-wet-wall" element={<Navigate to="/pvc-wet-wall-panels" replace />} />
        <Route path="/pvc-wet-wall/:productId" element={<RedirectWithProductId to="/pvc-wet-wall-panels" />} />
        <Route path="/shop/indoor-cladding/pvc-wet-wall" element={<Navigate to="/pvc-wet-wall-panels" replace />} />
        <Route path="/shop/indoor-cladding/pvc-wet-wall/:productId" element={<RedirectWithProductId to="/pvc-wet-wall-panels" />} />
        
        {/* Legacy WPC cladding redirects */}
        <Route path="/wpc-cladding" element={<Navigate to="/wpc-external-cladding" replace />} />
        <Route path="/wpc-cladding/external" element={<Navigate to="/wpc-external-cladding" replace />} />
        <Route path="/wpc-cladding/indoor" element={<Navigate to="/indoor-cladding" replace />} />
        <Route path="/wpc-cladding/product/:productId" element={<Navigate to="/wpc-external-cladding" replace />} />
        <Route path="/shop/wpc-cladding" element={<Navigate to="/wpc-external-cladding" replace />} />
        
        {/* Trade redirects */}
        <Route path="/trade" element={<Navigate to="/bulk-quote" replace />} />
        <Route path="/trade/apply" element={<Navigate to="/bulk-quote" replace />} />
        
        {/* Blog guide redirect */}
        <Route path="/artificial-grass-guide" element={<Navigate to="/blog/artificial-grass-guide" replace />} />
        
        {/* Old merged product redirects (SEO protection after Option A split) */}
        <Route path="/ps-cladding/ps-cladding-indoor-wall-panels" element={<Navigate to="/ps-cladding" replace />} />
        <Route path="/acoustic-cladding/acoustic-cladding-slat-wall-panels" element={<Navigate to="/acoustic-cladding" replace />} />
        <Route path="/uv-marble-sheets/uv-marble-sheet-panel-1200x2700x3mm" element={<Navigate to="/uv-marble-sheets" replace />} />
        <Route path="/product/ps-cladding-indoor-wall-panels" element={<Navigate to="/ps-cladding" replace />} />
        <Route path="/product/acoustic-cladding-slat-wall-panels" element={<Navigate to="/acoustic-cladding" replace />} />
        <Route path="/product/uv-marble-sheet-panel-1200x2700x3mm" element={<Navigate to="/uv-marble-sheets" replace />} />
        
        {/* Policy Pages */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsConditions />} />
        <Route path="/wpc-decking-warranty" element={<WpcDeckingWarranty />} />
        <Route path="/artificial-grass-warranty" element={<ArtificialGrassWarranty />} />
        <Route path="/hybrid-flooring-warranty" element={<HybridFlooringWarranty />} />
        <Route path="/refund-return-policy" element={<RefundReturnPolicy />} />
        <Route path="/shipping-delivery-policy" element={<ShippingDeliveryPolicy />} />
        
        {/* Catch any remaining /shop/:category */}
        <Route path="/shop/:category" element={<Navigate to="/" replace />} />
        
        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <LocationProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <AppContent />
          <DeferredMount timeout={1200}>
            <Suspense fallback={null}>
              <LocationSelectorModal />
            </Suspense>
          </DeferredMount>
          <DeferredMount timeout={4000}>
            <Suspense fallback={null}>
              <ChatBot />
            </Suspense>
          </DeferredMount>
          <DeferredMount timeout={5000}>
            <Suspense fallback={null}>
              <DiscountOfferPopup />
            </Suspense>
          </DeferredMount>
          
        </BrowserRouter>
      </LocationProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
